import {
  feedAddToBasket,
  feedAddToPayment,
  feedSetSomeId,
} from '~/actions/feed/utils/feedApi';
import {
  mailingAddToBasket,
  mailingAddToPayment,
  mailingChangeItemsCountInBasket,
  mailingClearBasket,
  mailingDeleteFromBasket,
  mailingGetEmail,
} from '~/actions/mailing/utils/mailingApi';
import {
  getUtmEmailLabel,
  refreshUtmLabels,
} from '~/clientEvent/utils/utmLabels';
import {
  GET_PARAM_FOR_ED_LETTER_UTM,
  KEY_EMAIL_FORWARDING_STORAGE,
} from '~/constants';
import generateUUID from '~/helpers/generateUUID';
import { saveToLocalStorage } from '~/helpers/storage';

/**
 * Задержка между проверками очереди window.sberLeadApiQueue
 */
const TIMEOUT_PROCESSING = 2000;

/**
 * Вызывает событие заданного типа
 * @param type
 * @param detail
 */
export const customEvent = (type: ClientEventType, detail: any) => {
  document.dispatchEvent(
    new CustomEvent(type, {
      bubbles: true,
      detail,
    })
  );
};

/**
 * Перезаписывает productId в формате string
 * @param productId
 * @returns
 */
const productIdWithCorrections = (productId: string | number = ''): string =>
  productId.toString();

/**
 * Перезаписывает productId в массиве items в формате string
 * @param items
 * @returns items
 */
const itemsWithCorrections = (items: IBasketItem[]): IBasketItem[] => {
  return items?.map((item) => {
    return {
      ...item,
      productId: productIdWithCorrections(item.productId),
    };
  });
};

/**
 * Обработчик сумматор для addToBasket
 *
 * @param productId - id продукта.
 * @param productCount - количество товара.
 * @param price - цена.
 * @param items - текущее состояние корзины, опционально.
 */
export const addToBasket = (
  productId: string = '',
  productCount: number = 1,
  price?: number,
  items?: IBasketItem[]
) => {
  const eventUuid = generateUUID();
  customEvent('add-to-basket', {
    eventUuid,
    productId: productIdWithCorrections(productId),
    quantity: productCount,
    price,
    items: itemsWithCorrections(items),
    utmFromEmail: getUtmEmailLabel(),
    ...feedAddToBasket(productIdWithCorrections(productId), productCount),
    ...mailingAddToBasket(
      productIdWithCorrections(productId),
      productCount,
      itemsWithCorrections(items),
      eventUuid
    ),
  });
};

/**
 * Обработчик сумматор для addToPayment
 * @param items - массив объектов { productId: "p189939", quantity: 1, price: 35.58 }
 * @param transactionId - идентификатор транзакции
 */
const addToPayment = (
  items: FeedOrderPaymentItem[] = [],
  transactionId: string = null
) => {
  const eventUuid = generateUUID();
  customEvent('add-to-payment', {
    eventUuid,
    items: itemsWithCorrections(items),
    transactionId,
    utmFromEmail: getUtmEmailLabel(),
    ...feedAddToPayment(itemsWithCorrections(items)),
    ...mailingAddToPayment(
      itemsWithCorrections(items),
      transactionId,
      eventUuid
    ),
  });
};

/**
 * Обработчик сумматор для setProductsInBasket
 * @param items
 */
const setProductsInBasket = (
  items: FeedOrderPaymentItem[],
  externalKey: string = null,
  updateFeed: boolean = true
) => {
  const eventUuid = generateUUID();
  customEvent('set-products-in-basket', {
    eventUuid,
    items: itemsWithCorrections(items),
    externalKey,
  });
  feedSetSomeId(itemsWithCorrections(items), externalKey, updateFeed, 'BY_OFFER');
};

/**
 * Обработчик для setProductsInPages
 * @param items
 */
const setProductsInPages = (
  items: FeedOrderPaymentItem[],
  externalKey: string = null,
  updateFeed: boolean = true
) => {
  feedSetSomeId(itemsWithCorrections(items), externalKey, updateFeed, 'BY_OFFER');
};

/**
 * Обработчик для setCategoryInPage
 * @param items
 */
const setCategoryInPage = (
  items: { categoryId: string }[],
  externalKey: string = null,
  updateFeed: boolean = true
) => {
  const convertedItems: IBasketItem[] = items.map((item) => {
    return {
      productId: item.categoryId,
    };
  });
  feedSetSomeId(itemsWithCorrections(convertedItems), externalKey, updateFeed, 'BY_CATEGORY');
};

/**
 * Обработчик удаления продукта из корзины
 *
 * @param productId - id продукта
 * @param items -
 */
const deleteFromBasket = (
  productId: string = '',
  items: IBasketItem[] = []
) => {
  const eventUuid = generateUUID();
  customEvent('delete-from-basket', {
    eventUuid,
    productId: productIdWithCorrections(productId),
    items: itemsWithCorrections(items),
    utmFromEmail: getUtmEmailLabel(),
    ...mailingDeleteFromBasket(
      productIdWithCorrections(productId),
      itemsWithCorrections(items),
      eventUuid
    ),
  });
};

/**
 * Изменения количества по товару в корзине
 *
 * @param productId - id продукта.
 * @param quantity - количество товара.
 * @param items - текущее состояние корзины, опционально.
 *
 * В результате выполнения функции на бэк передаются события FeedAddToBasket, MailingAddToBasket, MailingBasketContent
 */
const changeItemsCountInBasket = (
  productId: string = null,
  quantity: number = null,
  items: IBasketItem[] = []
) => {
  const eventUuid = generateUUID();
  customEvent('change-items-count-in-basket', {
    eventUuid,
    productId: productIdWithCorrections(productId),
    items: itemsWithCorrections(items),
    utmFromEmail: getUtmEmailLabel(),
    ...mailingChangeItemsCountInBasket(
      productIdWithCorrections(productId),
      quantity,
      itemsWithCorrections(items),
      eventUuid
    ),
  });
};

/**
 * Вызывает событие очистки корзины
 * @params items - текущее состояние корзины.
 */
const clearBasket = (items: IBasketItem[] = []) => {
  const eventUuid = generateUUID();
  customEvent('clear-basket', {
    eventUuid,
    items: itemsWithCorrections(items),
    utmFromEmail: getUtmEmailLabel(),
    ...mailingClearBasket(itemsWithCorrections(items), eventUuid),
  });
};

/**
 * Событие получения данных пользователя
 *
 * @param params
 */
const userIdentified = (userData: IUserDataForEvent) => {
  customEvent('user-identified', userData);
  mailingGetEmail(userData);
};

/**
 * Обрабатывает переменную sberLeadApiQueue
 */
const processingSberLeadApiQueue = () => {
  if (window.sberLeadApiQueue && window.sberLeadApiQueue.length > 0) {
    while (window.sberLeadApiQueue.length > 0) {
      const queueElement = window.sberLeadApiQueue.shift();
      switch (queueElement.method) {
        case 'addToPayment': {
          const { transactionId, items } =
            queueElement as IAddToPaymentArguments;
          addToPayment(items, transactionId);
          break;
        }
        case 'setProductsInBasket': {
          const {
            items,
            externalKey,
            updateFeed = false,
          } = queueElement as ISetProductsInBasketArguments;
          setProductsInBasket(items, externalKey, updateFeed);
          break;
        }
        case 'setProductsInPages': {
          const {
            items,
            externalKey,
            updateFeed = false,
          } = queueElement as ISetProductsInBasketArguments;
          setProductsInPages(items, externalKey, updateFeed);
          break;
        }
        case 'setCategoryInPage': {
          const {
            items,
            externalKey,
            updateFeed = false,
          } = queueElement as ISetCategoryInPageArguments;
          setCategoryInPage(items, externalKey, updateFeed);
          break;
        }

        default:
          // Тут можно логировать неудачные попытки
          break;
      }
    }
  }
};

/**
 * Проверяет GET-параметр sberlead_letter_utm.
 * Записывает в хранилище факт перехода по ссылке из письма
 */
export const urlControl = (): void => {
  const url = new URL(document.location.href);
  const sberleadLetterUtm = url.searchParams.get(GET_PARAM_FOR_ED_LETTER_UTM);
  if (sberleadLetterUtm) {
    saveToLocalStorage(
      KEY_EMAIL_FORWARDING_STORAGE,
      GET_PARAM_FOR_ED_LETTER_UTM,
      sberleadLetterUtm
    );
  }
};

/**
 * Инициализирует запуск обработчиков клиентских событий
 */
export const initClientEventApi = () => {
  window.addEventListener('popstate', urlControl);
  urlControl();
  refreshUtmLabels();
  processingSberLeadApiQueue();
  window.sberLeadApi = {
    addToBasket,
    addToPayment,
    setProductsInBasket,
    deleteFromBasket,
    changeItemsCountInBasket,
    clearBasket,
    userIdentified,
    setProductsInPages,
    setCategoryInPage,
  };
  setInterval(processingSberLeadApiQueue, TIMEOUT_PROCESSING);
};
